/**
 * Наблюдение за столбцом, доской и проектом — общее для сайта и приложения.
 *
 * Подписка охватывает существующие и будущие задачи объекта; уведомления —
 * только о событиях после её подключения. Наблюдение прав не даёт:
 * подписаться можно лишь на доступное, а уведомления приходят по задачам,
 * которые человек вправе видеть сейчас. Наблюдение за задачей (TASK) —
 * прежние «Наблюдатели» карточки: набор поводов у него постоянный.
 */

import { WatchEvent, WatchTargetType } from "@taskflow/contracts";

import { api } from "../api";

export interface WatchItem {
  targetType: WatchTargetType;
  targetId: string;
  /** Название объекта; у задачи — её название. null — объект удалён. */
  name: string | null;
  /** Номер задачи — только у TASK. */
  taskKey?: string;
  boardId: string | null;
  boardName: string | null;
  events: WatchEvent[];
  /** Объект виден сейчас; нет — уведомления не приходят, подписку стоит снять. */
  accessible: boolean;
  archived?: boolean;
  createdAt: string;
}

export interface WatchList {
  data: WatchItem[];
  /** Что выбрано в новой подписке. */
  defaultEvents: WatchEvent[];
}

export interface WatchTarget {
  targetType: WatchTargetType;
  targetId: string;
}

export const watchingApi = api.injectEndpoints({
  endpoints: (build) => ({
    watchList: build.query<WatchList, void>({
      query: () => ({ method: "watch.list" }),
      providesTags: ["Watch"],
    }),
    setWatch: build.mutation<WatchList, WatchTarget & { events?: WatchEvent[] }>({
      query: (params) => ({ method: "watch.set", params }),
      // Ответ — уже новый список: лишний запрос не нужен.
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        await storeReply(dispatch, queryFulfilled);
      },
    }),
    removeWatch: build.mutation<WatchList, WatchTarget>({
      query: (params) => ({ method: "watch.remove", params }),
      // Снятие наблюдения за задачей видно и в её карточке.
      invalidatesTags: (_result, _error, params) =>
        params.targetType === WatchTargetType.TASK ? ["Task"] : [],
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        await storeReply(dispatch, queryFulfilled);
      },
    }),
  }),
});

/** Ответ мутации — уже новый список «Моих подписок»: кладётся в кэш как есть.
 *  Ошибку покажет вызывающий через unwrap(), здесь её глотаем. */
async function storeReply(
  dispatch: (action: unknown) => unknown,
  queryFulfilled: Promise<{ data: WatchList }>,
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    await dispatch(watchingApi.util.upsertQueryData("watchList", undefined, data));
  } catch {
    // Отказ сервера обрабатывает вызывающий.
  }
}

export const { useWatchListQuery, useSetWatchMutation, useRemoveWatchMutation } = watchingApi;

/** Поводы в порядке показа и их подписи — «что сообщать». */
export const WATCH_EVENT_LABELS: Record<WatchEvent, string> = {
  [WatchEvent.TASK_CREATED]: "Создание задачи",
  [WatchEvent.COMMENT]: "Новые комментарии",
  [WatchEvent.STATUS]: "Изменение статуса",
  [WatchEvent.ASSIGNEES]: "Изменение исполнителей",
  [WatchEvent.DUE_DATE]: "Изменение срока",
  [WatchEvent.OVERDUE]: "Наступление просрочки",
  [WatchEvent.MOVED]: "Перемещение",
  [WatchEvent.ARCHIVED]: "Архивирование",
  [WatchEvent.DELETED]: "Удаление",
};

export const WATCH_EVENTS = Object.keys(WATCH_EVENT_LABELS) as WatchEvent[];

/** Умолчание до ответа сервера — то же, что у него (WATCH_DEFAULT_EVENTS). */
export const DEFAULT_WATCH_EVENTS: WatchEvent[] = [
  WatchEvent.TASK_CREATED,
  WatchEvent.COMMENT,
  WatchEvent.STATUS,
  WatchEvent.OVERDUE,
];

/** «Наблюдать за столбцом» — чем является объект, в творительном падеже. */
export const WATCH_TARGET_LABELS: Record<WatchTargetType, string> = {
  [WatchTargetType.TASK]: "задачей",
  [WatchTargetType.COLUMN]: "столбцом",
  [WatchTargetType.BOARD]: "доской",
  [WatchTargetType.PROJECT]: "проектом",
};

/** Подпись типа объекта в списке «Мои подписки». */
export const WATCH_KIND_LABELS: Record<WatchTargetType, string> = {
  [WatchTargetType.TASK]: "Задача",
  [WatchTargetType.COLUMN]: "Столбец",
  [WatchTargetType.BOARD]: "Доска",
  [WatchTargetType.PROJECT]: "Проект",
};

export function findWatch(
  list: WatchList | undefined,
  targetType: WatchTargetType,
  targetId: string | null | undefined,
): WatchItem | undefined {
  if (list === undefined || targetId === null || targetId === undefined) return undefined;
  return list.data.find((item) => item.targetType === targetType && item.targetId === targetId);
}

/** Строка списка: «Столбец «В работе» · Доска ОА». */
export function watchTitle(item: WatchItem): string {
  const name = item.taskKey ? `${item.taskKey} · ${item.name ?? ""}` : (item.name ?? "Объект удалён");
  return item.targetType === WatchTargetType.COLUMN && item.boardName
    ? `${name} · ${item.boardName}`
    : name;
}

/** Куда ведёт строка списка: задача, доска; у проекта перехода нет. */
export function watchLink(item: WatchItem): string | null {
  if (item.targetType === WatchTargetType.TASK && item.boardId && item.taskKey) {
    return `/boards/${item.boardId}/tasks/${item.taskKey}`;
  }
  return item.boardId ? `/boards/${item.boardId}` : null;
}
